import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LoginStoreEffects } from '@app/root-store/login-store/effects';
import { RelationshipStoreEffects } from '@app/root-store/relationship-store/effects';
import { VaultStoreEffects } from '@app/root-store/vault-store/effects';
import { UserPermissionService, UserService } from '@app/shared/services';
import { Store } from '@ngrx/store';
import { map, startWith } from 'rxjs/operators';
import * as selectors from '../../../../../root-store/relationship-store/relationship.selector';
import { Subscription } from 'rxjs';
import { ShareService } from '@app/shared/services/vault/share.service';
import { SharedVaultService } from '@app/shared/services/vault/shared-vault.service';
import { SharedConfirmationDialogComponent } from '@app/shared/component/shared-confirmation-dialog/shared-confirmation-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { AccessibilitySettingsComponent } from '@app/shared/component/accessibility-settings/accessibility-settings.component';
import { stringLocalDateToDatabase } from '@app/shared/utils';
import { FileEncryptedSuccessComponent } from '../../dialogs/file-encrypted-success/file-encrypted-success.component';
import { EventNames, EVENTS } from '@app/constants/events.constants';
import { NotifyAPIUpdateService } from '@app/shared/services/notify-api-update/notify-api-update.service';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'manage-permission-dialog',
  templateUrl: './manage-permission-dialog.component.html',
  styleUrls: ['./manage-permission-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagePermissionDialogComponent implements OnInit, OnDestroy {
  connectionUserFormControl = new UntypedFormControl();
  subscription = new Subscription();

  inlineComponent: boolean = false;
  showLoader: boolean = true;
  existingUsers: any;
  sharedUsers: any;
  users: any;
  selectedUser: any;
  isUserSelected: boolean = false;
  currentUser: any;
  relationshipObject$;
  relationshipObject;
  allUser;
  vaultStateObject$;
  vaultStateObjectData;
  markSharingAsComplete: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ManagePermissionDialogComponent>,
    private cd: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private loginService: LoginStoreEffects,
    private relationshipService: RelationshipStoreEffects,
    private sharedVaultService: SharedVaultService,
    private vaultService: VaultStoreEffects,
    private store: Store,
    private vaultShareService: ShareService,
    private toastr: ToastrService,
    private _userService: UserService,
    private _notifyAPIUpdateService: NotifyAPIUpdateService
  ) {
    this.subscription.add(
      this.loginService.loginObject.subscribe((state) => {
        if (state?.data) {
          this.currentUser = state?.data;
        }
      })
    );

    this.vaultStateObject$ = this.vaultService.vaultObject;

    this.subscription.add(
      this.vaultStateObject$.subscribe((item) => {
        this.vaultStateObjectData = item;
      })
    );
  }

  async ngOnInit() {
    this.relationshipObject$ = this.relationshipService.relationshipObject;
    this.subscription.add(
      this.connectionUserFormControl.valueChanges
        .pipe(
          startWith(''),
          map((value) => this.test(value))
        )
        .subscribe((response) => {
          this.users = response;
        })
    );
    if (this.data) this.markSharingAsComplete = this.data?.markAsCompleteStatus === 'complete' ? true : false;

    this.intiData();
  }

  async intiData() {
    try {
      if (this.data.generalPermission) {
        await this.getVaultRegisterMembers();
      } else {
        await this.getExistingUser();
        await this.getSharedFormUserList();
      }
    } catch (error) {
      this.toastr.error('An error occurred during initialization.');
    }
  }

  test(value) {
    if (this.allUser) {
      return this.allUser.filter((user) => user.full_name.toLowerCase().includes(value.toLowerCase()));
    }
  }

  trackEventsInMixPanel(events: { eventName: EventNames; data?: any }[]) {
    this._userService.trackEventsInMixPanel(events).then((response) => {});
  }

  updateExistingUser(value, existingUser) {
    if (this.data.generalPermission) {
      this.subscription.add(
        this.sharedVaultService
          .updateVaultRegisterMember(
            {
              share_date: stringLocalDateToDatabase(value.share_date),
              is_sealed: value.is_sealed,
              is_editable: value.is_editable,
              end_date: value.end_date._i ? stringLocalDateToDatabase(value.end_date) : null,
            },
            existingUser.id
          )
          .subscribe(
            async () => {
              await this.intiData();
              //refresh modal
              this.cd.markForCheck();
            },
            (error) => {
              // error handling
              this.toastr.error('An error occurred while updating the user.');
            }
          )
      );
    } else {
      this.subscription.add(
        this.vaultShareService
          .updateFormShare({
            user_id: this.data?.file?.user_id ? this.data.file.user_id : this.vaultStateObjectData.userId,
            id: existingUser.vault_user_share_id,
            share_date: stringLocalDateToDatabase(value.share_date),
            is_sealed: value.is_sealed,
            is_editable: value.is_editable,
            end_date: value.end_date._i ? stringLocalDateToDatabase(value.end_date) : null,
          })
          .subscribe(
            async (results) => {
              this.toastr.success(`${existingUser.user_first_name}'s access updated for ${this.data.file.form_name} File `);
              this.trackEventsInMixPanel([
                {
                  eventName: EVENTS[`template_view_edit_share`],
                  data: { shared_form_name: this.data.file.form_name, shared_with_user_name: existingUser.user_first_name },
                },
              ]);
              await this.intiData();
              //refresh modal
              this.cd.markForCheck();
            },
            (error) => {
              this.toastr.error(error);
            }
          )
      );
    }
  }

  deleteExistingUser(existingUser) {
    const sharedDialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete',
        materialIcon: 'delete',
        text: `Are you sure you want to delete this user's access to this ${this.data?.fileFolderName}?`,
        proceedTitle: 'Delete',
      },
    });

    this.subscription.add(
      sharedDialogRef.afterClosed().subscribe((confirmation) => {
        if (confirmation) {
          if (this.data.generalPermission) {
            this.deleteRegisterPermission(existingUser);
          } else {
            this.subscription.add(
              this.vaultShareService
                .deleteFormShare({
                  user_id: this.data?.file?.user_id ? this.data.file.user_id : this.vaultStateObjectData.userId,
                  id: existingUser.vault_user_share_id,
                })
                .subscribe(
                  (results) => {
                    this.toastr.success(`${existingUser.user_first_name}'s access removed for ${this.data.file.form_name} `);
                    this.trackEventsInMixPanel([
                      {
                        eventName: EVENTS[`template_view_remove_share`],
                        data: { shared_form_name: this.data.file.form_name, shared_with_user_name: existingUser.user_first_name },
                      },
                    ]);
                    this.intiData();
                    //this.getAllConnections();

                    //refresh modal
                    this.cd.markForCheck();
                  },
                  (error) => {
                    this.toastr.error('An error occurred while removing form share.');
                  }
                )
            );
          }
        }
      })
    );
  }

  setShareDetails(sharedUsers) {
    return {
      ...sharedUsers,
      end_date: sharedUsers.vault_user_share_end_date ? sharedUsers.vault_user_share_end_date : null,
      share_date: sharedUsers.vault_user_share_share_date ? sharedUsers.vault_user_share_share_date : null,
      is_sealed: sharedUsers.vault_user_share_is_sealed ? sharedUsers.vault_user_share_is_sealed : null,
      is_editable: sharedUsers.vault_user_share_is_editable ? sharedUsers.vault_user_share_is_editable : null,
    };
  }

  allConnection(users) {
    this.subscription.add(
      this.store.select(selectors.getAllUserListWithApprovedPending()).subscribe((data) => {
        const finalData = [];
        if (data == null || data == undefined) {
          data = [];
        }
        data.forEach((element) => {
          if (users.findIndex((x) => x.user_id == element.user_id) == -1) {
            var test = JSON.parse(JSON.stringify(element));
            test.id = element?.user_id;
            test.name = element.user_first_name + ' ' + element.user_last_name;
            Object.preventExtensions(test);
            finalData.push(test);
          }
        });
        this.manipulateDate(finalData);
      })
    );
  }

  manipulateDate(data) {
    this.cd.markForCheck();
    this.users = [
      ...data.map(({ id, user_id, name, from_user_id, relationship_name, user_profile_image_url, connection_status, owner_type }) => ({
        id,
        user_id,
        name,
        from_user_id,
        relationship_name,
        user_profile_image_url,
        connection_status,
        owner_type,
      })),
    ];
    this.allUser = [...data];
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  async getVaultRegisterMembers() {
    await new Promise<void>((resolve) => {
      this.subscription.add(
        this.sharedVaultService.getVaultRegisterMembers(this.data.origin).subscribe(
          (data: any) => {
            data.forEach((element) => {
              element.name = element?.first_name + ' ' + element?.last_name;
              element.user_first_name = element?.first_name;
              element.user_last_name = element?.last_name;
            });
            this.existingUsers = [...data];
            this.subscription.add(
              this.store.select(selectors.getrelationshipnameList(this.existingUsers)).subscribe((item) => {
                this.existingUsers = [...item];
                this.allConnection(this.existingUsers);
              })
            );
            this.showLoader = false;
            this.cd.markForCheck();
            resolve();
          },
          (error: any) => {
            this.toastr.error('An error occurred while fetching vault register members.');
            this.allConnection(this.existingUsers);
          }
        )
      );
    });
  }

  async getExistingUser() {
    let vault_user_form_id = this.data.file.vault_user_form_id;
    await new Promise<void>((resolve) => {
      this.subscription.add(
        this.sharedVaultService.getNonVaultPermissionData(vault_user_form_id).subscribe(
          (data: any) => {
            data.forEach((element, index) => {
              element.name = element?.first_name + ' ' + element?.last_name;
              element.type = 'OWNER';
            });
            this.existingUsers = [...data];
            resolve();
            this.showLoader = false;
          },
          (error: any) => {
            this.allConnection(this.existingUsers);
          }
        )
      );
    });
  }

  selectConnectedUser(userData) {
    this.selectedUser = userData;
    this.isUserSelected = true;
    const accessibilityDialog = this.dialog.open(AccessibilitySettingsComponent, {
      data: {
        sharedObjectType: 'file',
        shareDetails: null,
        profile_image: this.selectedUser.user_profile_image_url,
        name: this.selectedUser?.name,
        fromAutoComplete: true,
        origin: this.data?.origin,
        data: this.data,
        userData: userData,
      },
      // width: '250px',
      // height: '380px',
      panelClass: 'full-window-dialog-2',
      autoFocus: false,
    });

    this.subscription.add(
      accessibilityDialog.afterClosed().subscribe(async (res) => {
        if (res) {
          if (res.isPermissionTypeOwner) {
            this.intiData();
          } else {
            if (this.data.generalPermission) {
              this.saveVaultRegisterMember(res);
            } else {
              this.shareForm(res);
            }
          }
        }
      })
    );
  }

  async saveVaultRegisterMember(newShareTypeDetails) {
    this.subscription.add(
      this.sharedVaultService
        .saveVaultRegisterMember({
          connection_status: this.selectedUser.connection_status,
          user_id: this.selectedUser.user_id,
          end_date: newShareTypeDetails.end_date._i ? stringLocalDateToDatabase(newShareTypeDetails.end_date) : null,
          share_date: newShareTypeDetails.share_date ? stringLocalDateToDatabase(newShareTypeDetails.share_date) : null,
          is_sealed: newShareTypeDetails.is_sealed,
          is_editable: newShareTypeDetails.is_editable,
          register_type: this.data.origin,
          owner_type: 'share',
        })
        .subscribe(
          async (results) => {
            await this.intiData();
            //clear selection from mat auto complete
            this.clearAutoCompleteValue();
            //refresh modal
            this.cd.detectChanges();
          },
          (error) => {
            this.toastr.error(error);
          }
        )
    );
  }

  async shareForm(newShareTypeDetails) {
    this.subscription.add(
      this.vaultShareService
        .shareForm({
          connection_status: this.selectedUser.connection_status,
          user_id: this.data?.file?.user_id || this.currentUser.id,
          shared_with_user_id: this.selectedUser.user_id,
          end_date: newShareTypeDetails.end_date._i ? stringLocalDateToDatabase(newShareTypeDetails.end_date) : null,
          share_date: newShareTypeDetails.share_date ? stringLocalDateToDatabase(newShareTypeDetails.share_date) : null,
          is_sealed: newShareTypeDetails.is_sealed,
          vault_user_form_id: this.data.file?.vault_user_form_id,
          is_editable: newShareTypeDetails.is_editable,
        })
        .subscribe(
          async (results) => {
            if (results?.securityKey) {
              this.dialog.open(FileEncryptedSuccessComponent, {});
            }
            this.toastr.success(` ${this.data.file.form_name} File shared with   ${this.selectedUser?.name}.`);
            this.trackEventsInMixPanel([
              {
                eventName: EVENTS[`template_non_vault_form_add_share`],
                data: { shared_form_name: this.data.file.form_name, shared_with_user_name: this.selectedUser?.name },
              },
            ]);
            await this.intiData();

            //clear selection from mat auto complete
            this.clearAutoCompleteValue();

            //refresh modal
            this.cd.detectChanges();
          },
          (error) => {
            this.toastr.error(error);
          }
        )
    );
  }

  async getSharedFormUserList() {
    //get a list of existing users who have been shared this form
    let currentFormId = this.data.file?.vault_user_form_id;
    let userId = this.data?.file?.user_id ? this.data.file.user_id : this.vaultStateObjectData.userId;

    await new Promise<void>((resolve) => {
      this.subscription.add(
        this.vaultShareService.getSharedFormUserList(currentFormId, userId).subscribe(
          (results) => {
            results.forEach((element, index) => {
              element.name = element?.user_first_name + ' ' + element?.user_last_name;
              element.type = 'SHARED';
            });
            this.existingUsers = [...this.existingUsers, ...results];
            this.subscription.add(
              this.store.select(selectors.getrelationshipnameList(this.existingUsers)).subscribe((item) => {
                this.existingUsers = [...item];
                this.allConnection(this.existingUsers);
              })
            );
            this.showLoader = false;
            this.cd.markForCheck();
          },
          (error) => {
            this.toastr.error(error);
          }
        )
      );
    });
  }

  clearAutoCompleteValue() {
    this.selectedUser = null;
    this.isUserSelected = false;
    this.connectionUserFormControl.setValue('');

    setTimeout(() => {
      //wait a bit and remove focus from auto complete
      const nativeUserControl: any = document.querySelector('.mat-autocomplete-trigger');
      nativeUserControl.blur();
    }, 100);
  }

  deleteRegisterPermission(params) {
    this.subscription.add(
      this.sharedVaultService.removeVaultRegisterMember(params.id).subscribe(
        (res) => {
          this.toastr.success(`${params.user_first_name}'s access removed for ${this.data.fileFolderName} `);
          this.intiData();
        },
        (error) => {
          this.toastr.error(error);
        }
      )
    );
  }

  deletpermission(params) {
    const sharedDialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete',
        materialIcon: 'delete',
        text: `Are you sure you want to delete this user's access to this ${this.data?.fileFolderName}?`,
        proceedTitle: 'Delete',
      },
    });
    this.subscription.add(
      sharedDialogRef.afterClosed().subscribe((confirmation) => {
        if (confirmation) {
          if (this.data.generalPermission) {
            this.deleteRegisterPermission(params);
          } else {
            let vault_user_form_id: any = this.data.file.vault_user_form_id;
            let user_id = params.user_id;
            this.subscription.add(
              this.sharedVaultService.deleteNonVaultPermissionData(vault_user_form_id, user_id).subscribe(
                (res) => {
                  this.toastr.success(`${params.first_name}'s access removed for ${this.data.file.form_name} `);
                  this.intiData();
                },
                (error) => {
                  this.toastr.error(error);
                }
              )
            );
          }
        }
      })
    );
  }

  onChange() {
    this.markSharingAsComplete = !this.markSharingAsComplete;
    let status = this.markSharingAsComplete ? 'complete' : 'created';
    this.updateEvent(status);
  }

  updateEvent(status) {
    let itemsToSetUp = this.data.itemsToSetupList.find((x) => x.type == 'share');
    this.data.itemsToSetUp.callActionUpdate(itemsToSetUp.id, status);
  }
}
